import { Formik } from 'formik'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CustomerUploadBusinessInformation } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { CustomerBusinessInformationFormValues } from './CustomerBusinessInformationForm.interface'
import { CustomerOnboardingBusinessInformationSchema } from './CustomerBusinessInformationForm.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import tw from 'twin.macro'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { showToast } from 'src/utils/toast'
import { Button } from 'src/components/shared/Button'
import { handleApiError } from 'src/utils/errorHandlers'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { states } from 'src/constants/states'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import {
  customerBusinessTypeRadioOptions,
  customerComplianceStepTitles,
  customerTaxIdTypeRadioOptions,
} from 'src/constants/customer/constants'
import {
  CustomerBusinessType,
  CustomerResubmitInfoType,
  CustomerTaxIdType,
} from 'src/constants/customer/enums'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { getPartner } from 'src/selectors/user'
import { customerComplianceStepIcons } from 'src/constants/customer/icons'
import { CustomerFormHeader } from '../shared/CustomerFormHeader'

const defaultInitialValues: CustomerBusinessInformationFormValues = {
  companyName: '',
  businessType: '',
  otherBusinessType: '',
  dotNumber: '',
  addressStreet1: '',
  addressStreet2: '',
  addressCity: '',
  addressState: '',
  addressPostalCode: '',
  taxIdType: '',
  ein: '',
  itin: '',
}

interface Props {
  stepNumber?: number
  stepCount?: number
  initialValues?: CustomerBusinessInformationFormValues
  onSubmit?: () => void
}

const ZIP_CODE_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/]
const EIN_MASK = [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
const ITIN_MASK = [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

export const CustomerBusinessInformationForm = ({
  initialValues,
  stepNumber,
  stepCount,
  onSubmit,
}: Props) => {
  const dispatch = useDispatch()

  const customer = useSelector(getPartner)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const isResubmit = !!initialValues

  const handleFormSubmit = async ({
    businessType,
    otherBusinessType,
    taxIdType,
    ein,
    itin,
    ...otherValues
  }: CustomerBusinessInformationFormValues) => {
    setIsLoading(true)

    const res: CustomerBusinessInformationFormValues = {
      ...otherValues,
      taxIdType,
      businessType: otherBusinessType || businessType,
    }

    if (taxIdType === CustomerTaxIdType.Ein) {
      res.ein = ein?.replace(/-/g, '')
    } else if (taxIdType === CustomerTaxIdType.Itin) {
      res.itin = itin?.replace(/-/g, '')
    }

    if (isResubmit) {
      delete res.companyName
    }

    try {
      await createAsyncAction(dispatch, CustomerUploadBusinessInformation.request(res))

      showToast('Business information submitted successfully', {
        variant: 'success',
      })

      if (onSubmit) {
        onSubmit()
      }
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={
        initialValues || { ...defaultInitialValues, companyName: customer?.displayName || '' }
      }
      onSubmit={handleFormSubmit}
      validationSchema={CustomerOnboardingBusinessInformationSchema}
    >
      {({ handleSubmit, values }) => (
        <div css={tw`w-[318px] bg-white rounded-lg`}>
          <CustomerFormHeader
            title={stepCount ? undefined : 'Resubmit info'}
            stepCount={stepCount}
            stepNumber={stepNumber}
          />
          <div css={tw`px-4 pt-6 py-2`}>
            {customerComplianceStepIcons[CustomerResubmitInfoType.BusinessInfo]}
            <Typography variant="h4" css={tw`mt-2 mb-2`}>
              {customerComplianceStepTitles[CustomerResubmitInfoType.BusinessInfo]}
            </Typography>
            <div css={tw`w-full h-[1px] bg-[#EDEDED]`} />
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mb-4 mt-2`}>
              Enter your information exactly how it appears on your IRS SS-4 Form.
            </Typography>
            <Typography
              css={tw`my-4 pb-2 border-0 border-b border-solid border-[#EDEDED]`}
              variant="h6"
            >
              General
            </Typography>
            {!isResubmit && (
              <FormikTextField
                name="companyName"
                css={tw`w-full mb-4`}
                label="Business name*"
                placeholder="Enter business name"
                inputProps={{
                  'data-testid':
                    'customer-account-onboarding-business-information-company-name-input',
                }}
              />
            )}
            <div css={tw`w-full mb-4`}>
              <FormikRadioGroup
                name="businessType"
                css={tw`w-full mb-4`}
                label="Business type*"
                options={customerBusinessTypeRadioOptions}
                groupDataTestId="customer-account-onboarding-business-information-business-type-radio-group"
                optionDataTestIdPrefix="customer-account-onboarding-business-information-business-type-option"
              />
            </div>
            {values.businessType === CustomerBusinessType.Other && (
              <FormikTextArea
                name="otherBusinessType"
                css={tw`w-full mb-4`}
                label="Other (business type)*"
                placeholder="Enter business type"
                inputProps={{
                  'data-testid':
                    'customer-account-onboarding-business-information-other-business-type-input',
                }}
              />
            )}
            <FormikTextField
              name="dotNumber"
              css={tw`w-full mb-4`}
              label="Dot number*"
              placeholder="Enter dot number"
              inputProps={{
                'data-testid': 'customer-account-onboarding-business-information-dot-number-input',
              }}
            />
            <Typography
              css={tw`my-4 pb-2 border-0 border-b border-solid border-[#EDEDED]`}
              variant="h6"
            >
              Address details
            </Typography>
            <FormikTextField
              name="addressStreet1"
              css={tw`w-full mb-4`}
              label="Address - 1*"
              placeholder="Enter address"
              inputProps={{
                'data-testid':
                  'customer-account-onboarding-business-information-address-street-1-input',
              }}
            />
            <FormikTextField
              name="addressStreet2"
              css={tw`w-full mb-4`}
              label="Address - 2"
              placeholder="Enter address"
              inputProps={{
                'data-testid':
                  'customer-account-onboarding-business-information-address-street-2-input',
              }}
            />
            <FormikTextField
              name="addressCity"
              css={tw`w-full mb-4`}
              label="City*"
              placeholder="Enter city"
              inputProps={{
                'data-testid':
                  'customer-account-onboarding-business-information-address-city-input',
              }}
            />
            <div css={tw`w-full mb-4`}>
              <FormikSelect
                name="addressState"
                label="State*"
                placeholder="Select state"
                options={states}
                inputProps={{
                  'data-testid':
                    'customer-account-onboarding-business-information-address-state-select-input',
                }}
              />
            </div>
            <FormikTextField
              name="addressPostalCode"
              css={tw`w-full mb-4`}
              label="Zip code*"
              mask={ZIP_CODE_MASK}
              placeholder="Enter zip code"
              inputProps={{
                'data-testid':
                  'customer-account-onboarding-business-information-address-postal-code-input',
              }}
            />
            <Typography
              css={tw`my-4 pb-2 border-0 border-b border-solid border-[#EDEDED]`}
              variant="h6"
            >
              Tax Identification
            </Typography>
            <div css={tw`w-full mb-4`}>
              <FormikRadioGroup
                name="taxIdType"
                label="Tax Identification Type*"
                options={customerTaxIdTypeRadioOptions}
                groupDataTestId="customer-account-onboarding-business-information-tax-id-type-radio-group"
                optionDataTestIdPrefix="customer-account-onboarding-business-information-tax-id-type-option"
              />
            </div>
            {values.taxIdType === CustomerTaxIdType.Ein && (
              <FormikTextField
                name="ein"
                css={tw`w-full mb-4`}
                label="EIN*"
                mask={EIN_MASK}
                placeholder="Enter EIN"
                inputProps={{
                  'data-testid': 'customer-account-onboarding-business-information-ein-input',
                }}
              />
            )}
            {values.taxIdType === CustomerTaxIdType.Itin && (
              <FormikTextField
                name="itin"
                css={tw`w-full mb-4`}
                label="ITIN*"
                mask={ITIN_MASK}
                placeholder="Enter ITIN"
                inputProps={{
                  'data-testid': 'customer-account-onboarding-business-information-itin-input',
                }}
              />
            )}
          </div>
          <div css={tw`px-4 py-3`}>
            <Button
              data-testid="customer-account-onboarding-business-information-submit-btn"
              variant="contained"
              size="small"
              loading={isLoading}
              className={tw`w-full`}
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}
